import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

// ── Field definitions ────────────────────────────────────────────────────

export const LABEL_FIELD_DEFS = [
  { key: "lot_id",        label: "Lot ID",               required: false, prefix: ""              },
  { key: "brand",         label: "Brand",                required: false, prefix: ""              },
  { key: "vintage",       label: "Vintage",              required: false, prefix: ""              },
  { key: "ava",           label: "AVA",                  required: false, prefix: ""              },
  { key: "variety",       label: "Variety",              required: false, prefix: ""              },
  { key: "fanciful_name", label: "Fanciful Name",        required: false, prefix: ""              },
  { key: "case_pack",     label: "Case Pack",            required: false, prefix: ""              },
  { key: "bottled_date",  label: "Bottled Date",         required: false, prefix: "Bottled: "    },
  { key: "bw_number",     label: "Bonded Winery (BW#)",  required: false, prefix: ""              },
  { key: "abv",           label: "Alcohol by Volume",    required: false, prefix: "Alc. by Vol. " },
  { key: "fda_number",    label: "FDA #",                required: false, prefix: "FDA# "         },
  { key: "upc",           label: "UPC Barcode",          required: false, prefix: ""              },
] as const;

/** Returns the full printed value for a field, including any fixed prefix. */
export function fieldDisplayValue(key: LabelFieldKey, value: string | undefined): string {
  if (!value) return "";
  const def = LABEL_FIELD_DEFS.find((d) => d.key === key);
  return (def?.prefix ?? "") + value;
}

export type LabelFieldKey = (typeof LABEL_FIELD_DEFS)[number]["key"];

// ── Types ────────────────────────────────────────────────────────────────

export interface FieldFormatting {
  font_size: "small" | "medium" | "large" | "xl";
  font_face: "gothic" | "roman";
  bold: boolean;
  alignment: "left" | "center" | "right";
  visible: boolean;
  x: number; // dots from left edge (203 DPI)
  y: number; // dots from top edge (203 DPI)
}

export type CaseLabelFields = Partial<Record<LabelFieldKey, string>>;
export type CaseLabelFormatting = Partial<Record<LabelFieldKey, FieldFormatting>>;

/** Position and size of an image on the label, all in dots (203 DPI). */
export interface LogoConfig {
  url: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CaseLabelFolder {
  id: string;
  org_id: string;
  name: string;
  created_at: string;
}

export interface CaseLabel {
  id: string;
  org_id: string;
  name: string;
  folder_id: string | null;
  label_size: "4x4" | "2x4";
  fields: CaseLabelFields;
  formatting: CaseLabelFormatting;
  logo?: LogoConfig | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateCaseLabelInput {
  name: string;
  folder_id: string | null;
  label_size: "4x4" | "2x4";
  fields: CaseLabelFields;
  formatting: CaseLabelFormatting;
  logo?: LogoConfig | null;
}

export interface UpdateCaseLabelInput {
  id: string;
  name?: string;
  folder_id?: string | null;
  label_size?: "4x4" | "2x4";
  fields?: CaseLabelFields;
  formatting?: CaseLabelFormatting;
  logo?: LogoConfig | null;
}

export const DEFAULT_FIELD_FORMATTING: FieldFormatting = {
  font_size: "medium",
  font_face: "gothic",
  bold: false,
  alignment: "left",
  visible: true,
  x: 20,
  y: 20,
};

// Dot height per font size + gap between lines (matches zpl.ts FONT_SPEC)
const FIELD_LINE_HEIGHT: Record<FieldFormatting["font_size"], number> = {
  small: 32, medium: 62, large: 86, xl: 124,
};

// Returns default formatting for all fields, stacked top-to-bottom.
// 2×4 labels are only 406 dots tall (vs. 812 for 4×4) — stacking all 12
// fields at the 4×4 "medium" step would push the bottom several fields past
// the canvas edge, where they're clipped by LabelCanvas's overflow-hidden
// and become impossible to click or drag back into view. Step down to
// "small" for 2×4 so every field lands inside the visible canvas.
export function defaultFormatting(size: "4x4" | "2x4" = "4x4"): CaseLabelFormatting {
  const fontSize: FieldFormatting["font_size"] = size === "2x4" ? "small" : "medium";
  const step = FIELD_LINE_HEIGHT[fontSize];
  const fmt: CaseLabelFormatting = {};
  let y = 20;
  LABEL_FIELD_DEFS.forEach((f) => {
    fmt[f.key] = { ...DEFAULT_FIELD_FORMATTING, x: 20, y, font_size: fontSize };
    y += step;
  });
  return fmt;
}

// ── Coordinate model ─────────────────────────────────────────────────────────
// x is the anchor point for the text block:
//   left-aligned  → x = left edge of text
//   center-aligned → x = visual center of text  (label center = 406)
//   right-aligned  → x = right edge of text     (right margin = 788)
// This matches the CSS transform model in LabelCanvas and the ^FB model in the edge fn.
const LABEL_W_DOTS_CONST      = 812;
const PRINT_RIGHT_MARGIN_CONST = 24;
const CENTER_X = Math.round(LABEL_W_DOTS_CONST / 2);         // 406
const RIGHT_X  = LABEL_W_DOTS_CONST - PRINT_RIGHT_MARGIN_CONST; // 788

// Upgrade labels saved before the anchor-point model was introduced.
// Old model: center/right fields had x = container left edge (~20).
// New model: x = visual anchor (center or right edge of text).
function migrateFormatting(formatting: CaseLabelFormatting): CaseLabelFormatting {
  const result = { ...formatting } as CaseLabelFormatting;
  for (const key of Object.keys(result) as LabelFieldKey[]) {
    const f = result[key];
    if (!f) continue;
    if (f.alignment === "center" && f.x < 200) {
      // Old visual center = x + (W - x - rightMargin) / 2
      result[key] = { ...f, x: Math.round(f.x + (LABEL_W_DOTS_CONST - f.x - PRINT_RIGHT_MARGIN_CONST) / 2) };
    } else if (f.alignment === "right" && f.x < 200) {
      result[key] = { ...f, x: RIGHT_X };
    }
  }
  return result;
}

// ── 4×4 template layout — LOCKED (Copper Bear reference, updated 2026-07-07) ─
//
// All positions are in dots at 203 DPI on 812×812 media.
// Font heights: small=24, medium=48, large=72, xl=110.
//
//  y=90   brand         xl bold   center         → bottom 200
//  y=230  vintage       medium    center         → bottom 292
//  y=292  variety       medium    center         → bottom 354
//  y=354  fanciful_name medium    center         → bottom 416
//  y=416  ava           medium    center         → bottom 478
//  y=560  lot_id        large bold center        → bottom 646
//  y=700  abv           small     left     ┐
//  y=700  case_pack     small     right    ┤ row 1 (→ bottom 724)
//  y=732  bottled_date  small     left     ┐
//  y=732  bw_number     small     right    ┘ row 2 (→ bottom 756)
//  y=764  fda_number    small     center     row 3, own row — avoids any
//                                            crowding against row 2 if every
//                                            field is filled (→ bottom 788)
//  bottom margin: 812−788 = 24 dots
//
// Rebuilt 2026-07-07 from a real Copper Bear label at Jeff's request: brand
// leads (largest element), vintage/variety/fanciful/ava form a compact
// medium-size block, lot_id moves below as a secondary identifier just above
// the footer. Does not alter any already-saved labels — only affects new
// labels created via the "Template 4×4" button.
export function templateFormatting4x4(): CaseLabelFormatting {
  const f = (overrides: Partial<FieldFormatting>): FieldFormatting =>
    ({ ...DEFAULT_FIELD_FORMATTING, ...overrides });
  return {
    brand:         f({ x: CENTER_X, y: 90,  font_size: "xl",     alignment: "center", bold: true  }),
    vintage:       f({ x: CENTER_X, y: 230, font_size: "medium", alignment: "center"              }),
    variety:       f({ x: CENTER_X, y: 292, font_size: "medium", alignment: "center"              }),
    fanciful_name: f({ x: CENTER_X, y: 354, font_size: "medium", alignment: "center"              }),
    ava:           f({ x: CENTER_X, y: 416, font_size: "medium", alignment: "center"              }),
    lot_id:        f({ x: CENTER_X, y: 560, font_size: "large",  alignment: "center", bold: true  }),
    abv:           f({ x: 20,       y: 700, font_size: "small",  alignment: "left"                }),
    case_pack:     f({ x: RIGHT_X,  y: 700, font_size: "small",  alignment: "right"               }),
    bottled_date:  f({ x: 20,       y: 732, font_size: "small",  alignment: "left"                }),
    bw_number:     f({ x: RIGHT_X,  y: 732, font_size: "small",  alignment: "right"               }),
    fda_number:    f({ x: CENTER_X, y: 764, font_size: "small",  alignment: "center"              }),
    upc:           f({ x: 306,      y: 20,  font_size: "small",  visible: false                   }),
  };
}

// Pre-positioned layout for 2×4 labels (812×406 dots, landscape).
// Rebuilt 2026-07-07 from a real test label at Jeff's request: brand leads
// (large bold, center), variety/fanciful/ava form a centered block below it,
// then a row of vintage (left) / lot_id (center) / case_pack (right), then
// two more small footer rows. Does not alter any already-saved labels — only
// affects new labels created via the "Template 2×4" button.
//
//  y=40   brand         large bold center          → bottom 112
//  y=118  variety       medium      center          → bottom 166
//  y=172  fanciful_name medium      center          → bottom 220
//  y=226  ava           medium      center          → bottom 274
//  y=284  vintage       medium      left      ┐
//  y=284  lot_id        medium      center    ┤ row 1 (→ bottom 332, same size as ava)
//  y=284  case_pack     small       right     ┘
//  y=326  bw_number     small       left      ┐
//  y=326  abv           small       right     ┘ row 2 (→ bottom 350)
//  y=358  bottled_date  small       left      ┐
//  y=358  fda_number    small       right     ┘ row 3 (→ bottom 382)
//  bottom margin: 406−382 = 24 dots
export function templateFormatting2x4(): CaseLabelFormatting {
  const f = (overrides: Partial<FieldFormatting>): FieldFormatting =>
    ({ ...DEFAULT_FIELD_FORMATTING, ...overrides });
  return {
    brand:         f({ x: CENTER_X, y: 40,  font_size: "large",  alignment: "center", bold: true  }),
    variety:       f({ x: CENTER_X, y: 118, font_size: "medium", alignment: "center"              }),
    fanciful_name: f({ x: CENTER_X, y: 172, font_size: "medium", alignment: "center"              }),
    ava:           f({ x: CENTER_X, y: 226, font_size: "medium", alignment: "center"              }),
    vintage:       f({ x: 20,       y: 284, font_size: "medium", alignment: "left"                }),
    lot_id:        f({ x: CENTER_X, y: 284, font_size: "medium", alignment: "center"              }),
    case_pack:     f({ x: RIGHT_X,  y: 284, font_size: "small",  alignment: "right"               }),
    bw_number:     f({ x: 20,       y: 326, font_size: "small",  alignment: "left"                }),
    abv:           f({ x: RIGHT_X,  y: 326, font_size: "small",  alignment: "right"               }),
    bottled_date:  f({ x: 20,       y: 358, font_size: "small",  alignment: "left"                }),
    fda_number:    f({ x: RIGHT_X,  y: 358, font_size: "small",  alignment: "right"               }),
    upc:           f({ x: 20,       y: 370, font_size: "small",  visible: false                   }),
  };
}

// ── Helpers ──────────────────────────────────────────────────────────────

async function getOrgId(): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  const { data } = await supabase
    .from("user_account")
    .select("org_id")
    .eq("external_auth_user", user!.id)
    .single();
  return data!.org_id as string;
}

// ── Queries ──────────────────────────────────────────────────────────────

export function useCaseLabels(folderFilter?: string) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["case-labels", folderFilter],
    queryFn: async () => {
      let query = supabase
        .from("case_labels" as any)
        .select("*")
        .order("updated_at", { ascending: false });

      if (folderFilter) {
        query = query.eq("folder_id", folderFilter);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []).map((l: CaseLabel) => ({ ...l, formatting: migrateFormatting(l.formatting) })) as CaseLabel[];
    },
    enabled: isAuthenticated && !authLoading,
  });
}

export function useCaseLabel(id: string | null) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["case-labels", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("case_labels" as any)
        .select("*")
        .eq("id", id!)
        .single();
      if (error) throw error;
      const label = data as CaseLabel;
      return { ...label, formatting: migrateFormatting(label.formatting) };
    },
    enabled: !!id && isAuthenticated && !authLoading,
  });
}

// ── Mutations ────────────────────────────────────────────────────────────

export function useCreateCaseLabel() {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (input: CreateCaseLabelInput) => {
      const org_id = await getOrgId();
      const { data: { user } } = await supabase.auth.getUser();

      const { data, error } = await supabase
        .from("case_labels" as any)
        .insert({ ...input, org_id, created_by: user!.id })
        .select()
        .single();
      if (error) throw error;
      return data as CaseLabel;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["case-labels"] });
      toast({ title: "Label saved" });
    },
    onError: (e: Error) =>
      toast({ title: "Error saving label", description: e.message, variant: "destructive" }),
  });
}

export function useUpdateCaseLabel() {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...updates }: UpdateCaseLabelInput) => {
      const { data, error } = await supabase
        .from("case_labels" as any)
        .update(updates)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as CaseLabel;
    },
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ["case-labels"] });
      qc.setQueryData(["case-labels", updated.id], updated);
      toast({ title: "Label updated" });
    },
    onError: (e: Error) =>
      toast({ title: "Error updating label", description: e.message, variant: "destructive" }),
  });
}

export function useDeleteCaseLabel() {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("case_labels" as any)
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["case-labels"] });
      toast({ title: "Label deleted" });
    },
    onError: (e: Error) =>
      toast({ title: "Error deleting label", description: e.message, variant: "destructive" }),
  });
}

export function useUploadCaseLabelLogo() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (file: File): Promise<string> => {
      const org_id = await getOrgId();
      const ext    = file.name.split(".").pop() ?? "png";
      const path   = `${org_id}/${crypto.randomUUID()}.${ext}`;

      const { error } = await supabase.storage
        .from("case-label-logos")
        .upload(path, file, { cacheControl: "3600", upsert: false });
      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from("case-label-logos")
        .getPublicUrl(path);

      return publicUrl;
    },
    onError: (e: Error) =>
      toast({ title: "Upload failed", description: e.message, variant: "destructive" }),
  });
}

export function useDuplicateCaseLabel() {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const org_id = await getOrgId();
      const { data: { user } } = await supabase.auth.getUser();

      const { data: source, error: fetchErr } = await supabase
        .from("case_labels" as any)
        .select("*")
        .eq("id", id)
        .single();
      if (fetchErr) throw fetchErr;

      const src = source as CaseLabel;
      const { data, error } = await supabase
        .from("case_labels" as any)
        .insert({
          org_id,
          created_by: user!.id,
          name: `Copy of ${src.name}`,
          folder_id: src.folder_id,
          label_size: src.label_size,
          fields: src.fields,
          formatting: src.formatting,
        })
        .select()
        .single();
      if (error) throw error;
      return data as CaseLabel;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["case-labels"] });
      toast({ title: "Label duplicated" });
    },
    onError: (e: Error) =>
      toast({ title: "Error duplicating label", description: e.message, variant: "destructive" }),
  });
}

// ── Folders ──────────────────────────────────────────────────────────────
// Case-labels-only folder concept for sidebar grouping. Not related to the
// app-wide `client` table used elsewhere for Client Management.

export function useCaseLabelFolders() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["case-label-folders"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("case_label_folder" as any)
        .select("*")
        .order("name");
      if (error) throw error;
      return (data || []) as CaseLabelFolder[];
    },
    enabled: isAuthenticated && !authLoading,
  });
}

export function useCreateCaseLabelFolder() {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (name: string) => {
      const org_id = await getOrgId();
      const { data, error } = await supabase
        .from("case_label_folder" as any)
        .insert({ org_id, name: name.trim() })
        .select()
        .single();
      if (error) throw error;
      return data as CaseLabelFolder;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["case-label-folders"] });
    },
    onError: (e: Error) =>
      toast({ title: "Error creating folder", description: e.message, variant: "destructive" }),
  });
}

export function useRenameCaseLabelFolder() {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const { data, error } = await supabase
        .from("case_label_folder" as any)
        .update({ name: name.trim() })
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as CaseLabelFolder;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["case-label-folders"] });
    },
    onError: (e: Error) =>
      toast({ title: "Error renaming folder", description: e.message, variant: "destructive" }),
  });
}

export function useDeleteCaseLabelFolder() {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("case_label_folder" as any)
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["case-label-folders"] });
      qc.invalidateQueries({ queryKey: ["case-labels"] });
    },
    onError: (e: Error) =>
      toast({ title: "Error deleting folder", description: e.message, variant: "destructive" }),
  });
}
